import React from "react";
import { Navigate } from "react-router-dom";
import { usePermission, usePermissions } from "hooks/usePermissions";
import { useAuth } from "hooks/useAuth";
import { ROUTES } from "constants/index";
import { PageId, ActionCode } from "types/index";
import { Spinner } from "components/ui";

interface PermissionRouteProps {
  children: React.ReactNode;
  pageId: PageId;
  actionCode: ActionCode;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  pageId,
  actionCode,
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: permissionsLoading, pages } = usePermissions();
  const hasPermission = usePermission(pageId, actionCode);

  if (authLoading || (permissionsLoading && pages.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!hasPermission) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
};

interface CompanyProfileRouteProps {
  children: React.ReactNode;
}

/** Ensures tenant can only open their own company profile URL. */
export const CompanyProfileRoute: React.FC<CompanyProfileRouteProps> = ({
  children,
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const { isLoading: permissionsLoading, pages } = usePermissions();
  const hasPermission = usePermission(PageId.COMPANY_PROFILE, ActionCode.VIEW);

  if (authLoading || (permissionsLoading && pages.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!hasPermission) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
};
